import React, { useEffect, useRef, useState } from 'react';
import { useAppSelector } from '../../app/hooks';
import {
  selectFeatureFlagHitl3,
  selectHitlExpressTimerMaxSeconds,
} from '../../redux/features/config/config.selector';
import { selectTaskAssignedSessionStartedAt } from '../../redux/features/taskRouter/taskRouter.selector';
import {
  selectFinalOrderSubmitted,
  selectSessionId,
} from '../../redux/features/sessionBoundary/sessionBoundary.selectors';
import { selectIsCarPresent } from '../../selectors/message';
import PulseMeterTile from './PulseMeterTile';

/**
 * MENU BOARD TIME (HITL 3.0 Pulse tile).
 *
 * Time since the session started, against the express-mode threshold. The
 * threshold comes from the Unleash `hitl_express_timer` `max_seconds` variant
 * (`selectHitlExpressTimerMaxSeconds`), NOT an env var.
 *
 * Rendered while hitl_3_0 is on AND a session is active (a valid session id): it
 * is the "how long has this car been here" gauge in the Pulse card. With no
 * active session it hides entirely.
 *
 *   • Session active, no car yet → 0:00, green, empty bars (default).
 *   • Car present             → counts up every second from the session start.
 *   • Car exits               → FREEZES at the elapsed value and holds it.
 *   • Finish/Cancel Order      → while the car is gone, the interaction is over,
 *     (car already gone)         so it resets to the 0:00 default. (While the car
 *                                is still present it keeps counting — no reset.)
 *   • Next car enters         → resets to 0:00 and counts again.
 *
 * Counting is driven by the ACTIVE SESSION (`sessionId`), exactly like the header
 * timer this replaces — NOT by `isCarPresent`. That matters for task-router jobs:
 * on pickup `sessionId` + `session_started_at` are set (via setSessionStart on the
 * task_assigned menu load) before the `isCarPresent` flag lands, so gating on the
 * car flag would miss the session-start anchor. The anchor is the task_assigned
 * `session_started_at` when present (for a task-router job this is effectively the
 * car_enter moment), otherwise the time the session became active.
 *
 * `isCarPresent` (car_enter → true, car_exit → false) is used ONLY to FREEZE: once
 * the car that was present leaves, the clock holds. It is deliberately a
 * was-present→now-gone transition so a not-yet-set flag at pickup does not read as
 * an exit.
 *
 * It does NOT blink over the limit — it escalates green -> amber -> red and caps
 * at red. M:SS, updated every second. Gated entirely by hitl_3_0.
 */

// Colour-band threshold used when the Unleash `max_seconds` variant isn't set,
// so the tile still escalates on every order.
const DEFAULT_MENU_BOARD_LIMIT_SECONDS = 120;

// M:SS, e.g. "0:07", "1:42".
const formatMinutesSeconds = (ms: number): string => {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds < 10 ? `0${seconds}` : seconds}`;
};

const MenuBoardTimeTile = () => {
  const isHitl3 = useAppSelector(selectFeatureFlagHitl3);
  const maxSeconds = useAppSelector(selectHitlExpressTimerMaxSeconds);
  const sessionStartedAt = useAppSelector(selectTaskAssignedSessionStartedAt);
  const sessionId = useAppSelector(selectSessionId);
  const isCarPresent = useAppSelector(selectIsCarPresent);
  const finalOrderSubmitted = useAppSelector(selectFinalOrderSubmitted);

  const limitSeconds =
    maxSeconds > 0 ? maxSeconds : DEFAULT_MENU_BOARD_LIMIT_SECONDS;

  const [elapsedMs, setElapsedMs] = useState(0);
  // The anchor we're currently counting from; non-null only while ticking. Used
  // to freeze at the exact elapsed value the instant the car leaves.
  const startTimeRef = useRef<number | null>(null);
  // Whether the car has been present at any point this session — so a car_exit is
  // told apart from the flag simply not having landed yet at task-router pickup.
  const carWasPresentRef = useRef(false);

  useEffect(() => {
    // No active session (idle, or the session just ended on Finish/Cancel) → back
    // to the 0:00 default, and clear the per-session freeze latch.
    if (!isHitl3 || !sessionId) {
      startTimeRef.current = null;
      carWasPresentRef.current = false;
      setElapsedMs(0);
      return undefined;
    }

    if (isCarPresent) {
      carWasPresentRef.current = true;
    }

    // FREEZE once the car that was present leaves: snap to the exact elapsed at
    // this instant and stop; the value holds (session is still active post-exit)
    // until the session ends or the next car. Gated on carWasPresent so a pickup
    // where isCarPresent hasn't landed yet does NOT read as an exit.
    if (carWasPresentRef.current && !isCarPresent) {
      if (startTimeRef.current != null) {
        setElapsedMs(Math.max(0, Date.now() - startTimeRef.current));
        startTimeRef.current = null;
      }
      return undefined;
    }

    // Count up from the session start. Anchor to the task_assigned
    // session_started_at when valid (the car-enter moment for a task-router job);
    // otherwise from now. Driven by the active session — not isCarPresent — so it
    // picks up session_started_at even before the car-present flag lands.
    const parsedStart = new Date(sessionStartedAt).getTime();
    const startTime =
      !Number.isNaN(parsedStart) && sessionStartedAt ? parsedStart : Date.now();
    startTimeRef.current = startTime;

    const tick = () => setElapsedMs(Math.max(0, Date.now() - startTime));
    tick(); // compute immediately so a past anchor is right on first paint
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [isHitl3, sessionId, isCarPresent, sessionStartedAt]);

  // Finish/Cancel Order while the car has already left ends the interaction, so
  // drop the frozen value back to the 0:00 default immediately (belt-and-suspenders
  // alongside the session-end reset above). `finalOrderSubmitted` pulses true on
  // any finalize/cancel; we act on that edge only when the car is gone — while it's
  // still present the clock keeps running.
  useEffect(() => {
    if (finalOrderSubmitted && !isCarPresent) {
      startTimeRef.current = null;
      carWasPresentRef.current = false;
      setElapsedMs(0);
    }
  }, [finalOrderSubmitted, isCarPresent]);

  // Only while a session is active (a valid session id). No session → hidden.
  if (!isHitl3 || !sessionId) {
    return null;
  }

  return (
    <PulseMeterTile
      label="Menu Board Time"
      elapsedMs={elapsedMs}
      limitMs={limitSeconds * 1000}
      formatValue={formatMinutesSeconds}
      testId="menu-board-time-tile"
    />
  );
};

export default MenuBoardTimeTile;
